import { isArray, hasObjectConstructor } from '@/utils/checkers/validation'
import forEach from '@/utils/forEach'
import cloneDeep from 'lodash.clonedeep'
import { get } from 'lodash'
import deepSearch from '@utils/deepSearch'
import { toDeepRaw } from '@/utils/proxyConverter'
import type { BlockContent } from '@/types/content'

export type Breadcrumb = Pick<BlockContent, '_uid' | 'component'> & {
  _parentfield?: string
  _parentindex?: number
  _parentName?: string
  _parentUid?: string
}

export default {
  cache: null,

  clearCache(): void {
    this.cache = null
  },

  /* This function retrieves a value from an object (obj) using a dot-separated key (key).
  It supports nested keys by traversing the object hierarch */
  dotGet(key, obj): unknown {
    if (key.indexOf('.') > -1) {
      return key.split('.').reduce((o, i) => o[i], obj)
    }

    return key in obj ? obj[key] : undefined
  },

  /* This function searches for a value (value) in a JSON object (json)
   based on a key (key). It returns an array containing the first
   occurrence of the matching value and its parent objects. */
  find(json, key, value): unknown {
    const components = this.getComponentMap(json)
    if (!components[value]) {
      // Finds it the old way
      this.clearCache()
      return deepSearch(json, key, value)
    }
    // Returns the root
    if (!components[value]?.materialized) {
      return [json]
    }

    const pureJsObject = toDeepRaw(json)

    const result = this.dotGet(components[value].materialized, pureJsObject)

    return [result]
  },

  /* This function is similar to find(), but it returns only the first
  occurrence of the matching value as an object, or an empty object
  if no match is found. */
  findFirst(json, key, value): unknown {
    const result = this.find(json, key, value)
    if (result.length > 0) {
      return result[0]
    }
    return {}
  },

  /* This function creates a deep clone of an object (obj) using the
  cloneDeep function from the Lodash library. It ensures that all nested
  objects and arrays are copied. */
  clone<T>(obj: T): T {
    return cloneDeep(obj)
  },

  /* This function replaces an object (json) with a new object (newobj)
  in the JSON structure based on a key (key) and value (value).
  It recursively searches for the matching key-value pair and performs the
  replacement. */
  replace(json, newobj, key, value): unknown {
    let replaced = false

    function replace(json, newobj, key, value): unknown {
      if (isArray(json) || hasObjectConstructor(json)) {
        forEach(json, (val, item) => {
          if (replaced) {
            return
          }

          if (item === key && value === json[item]) {
            forEach(json, (val, attrname) => {
              delete json[attrname]
            })

            forEach(newobj, (val, attrname1) => {
              json[attrname1] = newobj[attrname1]
            })

            replaced = true
          } else {
            replace(json[item], newobj, key, value)
          }
        })
      }
    }

    replace(json, newobj, key, value)

    return json
  },

  /* This function generates a component map from a JSON object (json).
  The component map is a hierarchical representation of the components
  present in the JSON structure, along with their unique identifiers
  and parent-child relationships. */
  getComponentMap(json): unknown {
    if (this.cache) {
      return this.cache
    }

    const components = {}
    components[json._uid] = {
      component: json.component,
      uid: json._uid,
    }

    const buildTree = function (tree, parent, field, materialized): unknown {
      if (isArray(tree) || hasObjectConstructor(tree)) {
        if (typeof materialized === 'undefined') {
          materialized = ''
        } else {
          materialized = (materialized ? materialized + '.' : '') + field
        }

        forEach(tree, (val, item) => {
          if (
            hasObjectConstructor(tree[item]) &&
            typeof tree[item].component !== 'undefined' &&
            typeof tree[item]._uid !== 'undefined' &&
            tree[item].component.indexOf('_table') !== 0
          ) {
            components[tree[item]._uid] = {
              component: tree[item].component,
              uid: tree[item]._uid,
              materialized: materialized + '.' + item,
              parent: {
                field,
                index: item,
                uid: parent,
              },
            }
          }
          if (
            hasObjectConstructor(tree) &&
            typeof tree.component !== 'undefined'
          ) {
            parent = tree._uid
          }
          buildTree(tree[item], parent, item, materialized)
        })
      }
    }
    buildTree(json, json._uid)

    if (json?._uid) {
      this.cache = components
    }

    return components
  },

  /* This function retrieves the breadcrumbs (path) leading
  to a specific value (value) in a JSON object (json). It uses
  the component map generated by getComponentMap() to traverse the
  parent-child relationships and construct the breadcrumb array. */
  getBreadcrumbs(json, key, value): Breadcrumb[] {
    const components = this.getComponentMap(json)
    let current = components[value]
    let breadcrumbs = []

    if (!current) {
      return []
    }

    const hasTheSameParentUid = current.uid === current?.parent?.uid
    if (hasTheSameParentUid) {
      current.parent = undefined
    }

    const firstBreadcrumb: Breadcrumb = {
      component: current.component,
      _uid: current.uid,
    }

    if (current.parent) {
      firstBreadcrumb._parentfield = current.parent.field
      firstBreadcrumb._parentindex = current.parent.index
      firstBreadcrumb._parentName = components[current.parent.uid].component
      firstBreadcrumb._parentUid = current.parent.uid
    }

    breadcrumbs = [firstBreadcrumb]

    while (typeof current.parent !== 'undefined') {
      current = components[current.parent.uid]

      const breadcrumb: Breadcrumb = {
        component: current.component,
        _uid: current.uid,
      }

      if (current.parent) {
        breadcrumb._parentfield = current.parent.field
        breadcrumb._parentindex = current.parent.index
        breadcrumb._parentName = components[current.parent.uid].component
        breadcrumb._parentUid = current.parent.uid
      }

      breadcrumbs.unshift(breadcrumb)
    }

    return breadcrumbs
  },

  /* This function flattens an array (array) by recursively concatenating
  all sub-arrays found at the specified key (key). It returns a new
  flattened array. */
  flatten(array, key): unknown {
    let result = []
    const func = this
    array.forEach(function (a) {
      result.push(a)
      if (Array.isArray(a[key])) {
        result = result.concat(func.flatten(a[key], key))
      }
    })
    return result
  },

  isArray(value: unknown): boolean {
    return Array.isArray(value)
  },

  /**
  This function checks if an iterable (array or string) (iterable)
  contains a specific value (value). It uses the indexOf() method
  to perform the inclusion check.
   */
  includes(iterable, value: unknown): boolean {
    if (typeof iterable.indexOf === 'function') {
      return iterable.indexOf(value) !== -1
    }

    return false
  },

  /**
   * This function sorts an array of objects ($array) based on the
   * values of a specified object field ($field). It uses the get()
   * function from the Lodash library to access nested fields.
   * Example 1:
   * items: [{ name: d }, { name: c }, { name: a }]
   * sortArrayByObjectField(items, 'name') // [{ name: a }, { name: c }, { name: d }]
   * Example 2: With nested objects
   * items: [{ user: { name: 'Arnold', surname: 'Schwarzenegger' } }, { user: { name: 'Dolph', surname: 'Lundgren' } }, { user: { name: 'Eva', surname: 'Green' } }]
   * sortArrayByObjectField(items, 'user.surname') // [{ user: { name: 'Eva', surname: 'Green' } }, { user: { name: 'Dolph', surname: 'Lundgren' } }, { user: { name: 'Arnold', surname: 'Schwarzenegger' } }]
   */
  sortArrayByObjectField<T>($array: T[], $field: string): T[] {
    return $array.sort(($a, $b) => {
      if (get($a, $field) < get($b, $field)) {
        return -1
      }
      if (get($a, $field) > get($b, $field)) {
        return 1
      }
      return 0
    })
  },

  move<T>(items: T[], oldIndex: number, newIndex: number): T[] {
    const itemRemovedArray = [
      ...items.slice(0, oldIndex),
      ...items.slice(oldIndex + 1, items.length),
    ]
    return [
      ...itemRemovedArray.slice(0, newIndex),
      items[oldIndex],
      ...itemRemovedArray.slice(newIndex, itemRemovedArray.length),
    ]
  },
}
